/**
 * @fileoverview Animated value component using spring physics for smooth number transitions
 * @module shared/components/ui/AnimatedValue
 */

import { useSpring, motion } from 'framer-motion'
import { ReactElement, useEffect, useState, useRef } from 'react'
import { cn } from '@/lib/utils'

/** Easing function type for animations */
type EasingType = 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | 'spring'

/** Easing configurations mapped to framer-motion spring options */
const easingConfigs: Record<EasingType, { stiffness: number; damping: number; mass: number }> = {
  linear: { stiffness: 100, damping: 100, mass: 1 },
  easeIn: { stiffness: 200, damping: 20, mass: 0.5 },
  easeOut: { stiffness: 300, damping: 30, mass: 0.8 },
  easeInOut: { stiffness: 100, damping: 20, mass: 1 },
  spring: { stiffness: 100, damping: 10, mass: 1 },
}

interface AnimatedValueProps {
  /** The target value to animate to */
  value: number
  /** Previous value to animate from (useful when parent tracks history across filter changes) */
  fromValue?: number
  /** Optional formatter function for display (e.g., currency formatting) */
  formatter?: (value: number) => string
  /** Optional prefix to prepend (e.g., '+' for positive profit) */
  prefix?: string
  /** Optional suffix to append */
  suffix?: string
  /** Animation duration in milliseconds */
  duration?: number
  /** Easing type for animation curve */
  easing?: EasingType
  /** Additional className for styling */
  className?: string
  /** Enable blur effect during animation (default: true) */
  blur?: boolean
  /** Duration of blur effect in ms (default: 300) */
  blurDuration?: number
  /** Enable shimmer text effect (animated gradient) */
  shimmer?: boolean
  /** Custom shimmer color variant (uses CSS variable if provided) */
  shimmerColor?: 'positive' | 'negative'
}

/**
 * Animates numeric values with spring physics when the value changes.
 * Ideal for dashboard metrics that update on filter changes.
 *
 * @component
 * @example
 * ```tsx
 * <AnimatedValue
 *   value={12500}
 *   fromValue={previousValue}
 *   formatter={(v) => formatMoney(v)}
 *   prefix="+"
 *   easing="easeOut"
 * />
 * ```
 */
export function AnimatedValue({
  value,
  fromValue,
  formatter,
  prefix = '',
  suffix = '',
  duration = 1500,
  easing = 'easeOut',
  className,
  blur = true,
  blurDuration = 300,
  shimmer = false,
  shimmerColor,
}: AnimatedValueProps): ReactElement {
  const [displayValue, setDisplayValue] = useState(fromValue ?? value)
  const [isBlurred, setIsBlurred] = useState(false)
  const prevValueRef = useRef(value)
  const targetValueRef = useRef(value)
  const isInitialMount = useRef(true)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const easingConfig = easingConfigs[easing]
  // Scale stiffness/damping based on duration (1500ms is baseline)
  const durationScale = 1500 / duration

  const springValue = useSpring(fromValue ?? value, {
    stiffness: easingConfig.stiffness * durationScale,
    damping: easingConfig.damping * durationScale,
    mass: easingConfig.mass,
  })

  useEffect(() => {
    const unsubscribe = springValue.on('change', (latest) => {
      setDisplayValue(Math.round(latest))
    })
    return unsubscribe
  }, [springValue])

  // Handle value changes
  useEffect(() => {
    targetValueRef.current = value

    // Skip initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      // Handle initial animation from fromValue
      if (fromValue !== undefined && fromValue !== value) {
        springValue.jump(fromValue)
      }
      springValue.set(value)
      prevValueRef.current = value
      return
    }

    // Trigger blur once when value changes (not on every spring tick)
    if (blur && prevValueRef.current !== value) {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Visual effect for value change, cleared by timeout
      setIsBlurred(true)
      blurTimeoutRef.current = setTimeout(() => {
        setIsBlurred(false)
      }, blurDuration)
    }

    prevValueRef.current = value

    // Animate spring
    springValue.set(value)
  }, [value, fromValue, springValue, blur, blurDuration])

  // Cleanup timeout on unmount only
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current)
    }
  }, [])

  const formattedValue = formatter ? formatter(displayValue) : displayValue.toString()
  const displayText = `${prefix}${formattedValue}${suffix}`

  if (shimmer) {
    // Dynamic gradient colors based on shimmerColor prop
    const gradientClass = shimmerColor === 'positive'
      ? 'from-positive via-positive/60 to-positive'
      : shimmerColor === 'negative'
        ? 'from-negative via-negative/60 to-negative'
        : 'from-current via-foreground/60 to-current'

    return (
      <motion.span
        className={cn(
          className,
          'transition-[filter] duration-150',
          'bg-gradient-to-r bg-[length:200%_100%] bg-clip-text text-transparent',
          gradientClass
        )}
        style={{ filter: isBlurred ? 'blur(3px)' : 'none' }}
        animate={{
          backgroundPosition: ['200% center', '-200% center'],
        }}
        transition={{
          duration: 4,
          ease: 'linear',
          repeat: Infinity,
        }}
      >
        {displayText}
      </motion.span>
    )
  }

  return (
    <span
      className={cn(className, 'transition-[filter] duration-150')}
      style={{ filter: isBlurred ? 'blur(3px)' : 'none' }}
    >
      {displayText}
    </span>
  )
}
